import React, { useState } from 'react';
import {
  IonButton,
  IonIcon,
  IonItem,
  IonText,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonInput,
  IonLabel,
  IonSelect,
  IonSelectOption,
} from '@ionic/react';
import { create, add, trash } from 'ionicons/icons';
import { DecisionTreeTableProps, EditModalData } from './types';
import { 
  getMergeCount, 
  isDefaultDecision, 
  getCurrentValue,
  getColumnDisplayName,
  getAvailableOptions,
  getUsedOptionsForColumnAtPath,
  getRemainingOption
} from './decision-tree-utils';
import { useTags } from '../../tags/tags-provider';
import { useParameters } from '../parameters-provider';

const DecisionTreeTable = <T,>({ 
  table, 
  onUpdate, 
  getColumnDisplayName: customGetColumnDisplayName,
  formatDecision: customFormatDecision,
  renderOutputCell,
  onEditOutput,
  onCreateSplit,
  onEditSplit,
  onDeleteBranch,
  onAddColumn,
  onDeleteColumn,
  paths = []
}: DecisionTreeTableProps<T> & { paths?: any[] }) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [modalData, setModalData] = useState<EditModalData | null>(null);
  const [newSplitValue, setNewSplitValue] = useState<string>('');
  
  const { tags } = useTags();
  const { parameters } = useParameters();
  
  // Use the enhanced column display name function
  const getColumnDisplayNameEnhanced = (column: any) => {
    return customGetColumnDisplayName ? 
      customGetColumnDisplayName(column) : 
      getColumnDisplayName(column, tags || [], parameters || []);
  };

  // Handle cell click - determine if it's create or edit
  const handleCellClick = (rowIndex: number, colIndex: number) => {
    const column = table.inputColumns[colIndex];
    const decision = paths[rowIndex]?.decisions[colIndex];
    const isDefault = isDefaultDecision(decision);
    const isBooleanColumn = column?.type === 'tags';
    
    // For boolean columns, create split directly without modal
    if (isBooleanColumn && !decision && onCreateSplit) {
      onCreateSplit(rowIndex, colIndex, 'true'); // Always create true/false split for boolean columns
      return;
    }
    
    // For other columns, open modal as before
    const currentValue = getCurrentValue(decision, column);
    
    setModalData({ 
      row: rowIndex, 
      col: colIndex, 
      column, 
      isDefault,
      currentValue
    });
    setNewSplitValue(currentValue);
    
    if (isDefault) {
      setIsCreateModalOpen(true);
    } else {
      setIsEditModalOpen(true);
    }
  };

  const normalizeSplitValue = (value: string, column: any): string => {
    if (column?.type !== 'ratio') {
      return value;
    }

    const trimmed = value.trim().replace('%', '');
    if (!trimmed) {
      return '';
    }

    const parsed = Number(trimmed);
    if (isNaN(parsed)) {
      return '';
    }

    return (parsed / 100).toString();
  };

  const isCellClickable = (rowIndex: number, colIndex: number): boolean => {
    const decision = paths[rowIndex]?.decisions[colIndex];
    const column = table.inputColumns[colIndex];
    const isDiscreteColumn = ['categorical', 'lightStatus', 'tags'].includes(column?.type);
    const isBooleanColumn = column?.type === 'tags';
    
    // For boolean columns with existing decisions, they're not clickable (only delete button)
    if (isBooleanColumn && decision) {
      return false;
    }
    
    // For boolean columns with no decision (not split yet), always clickable (no modal needed)
    if (isBooleanColumn && !decision) {
      return true;
    }
    
    // For other discrete columns with existing decisions, they're not clickable (only delete button)
    if (isDiscreteColumn && !isBooleanColumn && !isDefaultDecision(decision)) {
      return false;
    }
    
    // For other discrete columns with default decisions, check if there are available options
    if (isDiscreteColumn && !isBooleanColumn && isDefaultDecision(decision)) {
      const targetPath = paths[rowIndex];
      const usedOptions = getUsedOptionsForColumnAtPath(table.decisionTree, targetPath, colIndex);
      const availableOptions = getAvailableOptions(column, usedOptions, tags || [], parameters || []);
      
      // Only clickable if there are more than 1 available options
      return availableOptions.length > 1;
    }
    
    // For non-discrete columns, always clickable
    return true;
  };

  // Create new split condition
  const handleCreateSplit = () => {
    if (!modalData || !onCreateSplit) return;
    const normalizedValue = normalizeSplitValue(newSplitValue, modalData.column);
    onCreateSplit(modalData.row, modalData.col, normalizedValue);
    setIsCreateModalOpen(false);
    setModalData(null);
    setNewSplitValue('');
  };

  // Edit existing split condition
  const handleEditSplit = () => {
    if (!modalData || !onEditSplit) return;
    const normalizedValue = normalizeSplitValue(newSplitValue, modalData.column);
    onEditSplit(modalData.row, modalData.col, normalizedValue);
    setIsEditModalOpen(false);
    setModalData(null);
    setNewSplitValue('');
  };

  // Delete branch
  const handleDeleteBranch = () => {
    if (!modalData || !onDeleteBranch) return;
    onDeleteBranch(modalData.row, modalData.col);
    setIsEditModalOpen(false);
    setModalData(null);
    setNewSplitValue('');
  };

  if (table.inputColumns.length === 0) {
    return (
      <div style={{ width: '100%', maxWidth: 'none' }}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.2em', fontWeight: 'bold' }}>Decision Tree</h3>
        <div style={{ overflowX: 'auto', width: '100%' }}>
          <table style={{ width: '100%', minWidth: '600px', borderCollapse: 'collapse', border: '1px solid #ddd', tableLayout: 'fixed' }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                <th style={{ 
                  padding: '8px', 
                  border: '1px solid #ddd', 
                  textAlign: 'left',
                  width: '60%'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <IonText>
                      <strong>No Input Columns</strong>
                    </IonText>
                    {onAddColumn && (
                      <IonButton
                        fill="clear"
                        size="small"
                        onClick={onAddColumn}
                      >
                        <IonIcon icon={add} />
                        {' add column'}
                      </IonButton>
                    )}
                  </div>
                </th>
                <th style={{ 
                  padding: '8px', 
                  border: '1px solid #ddd', 
                  textAlign: 'left',
                  width: '40%'
                }}>
                  <IonText>
                    <strong>Output</strong>
                  </IonText>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ 
                  padding: '8px', 
                  border: '1px solid #ddd', 
                  verticalAlign: 'top',
                  backgroundColor: 'transparent'
                }}>
                  <IonText color="medium">
                    <p style={{ margin: 0, fontStyle: 'italic' }}>Add input columns to create decision branches</p>
                  </IonText>
                </td>
                <td style={{ 
                  padding: '8px', 
                  border: '1px solid #ddd', 
                  verticalAlign: 'top',
                  backgroundColor: 'transparent'
                }}>
                  {renderOutputCell(table.decisionTree.output, 0)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: 'none' }}>
      <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.2em', fontWeight: 'bold' }}>Decision Tree</h3>
      <div style={{ overflowX: 'auto', width: '100%' }}>
        <table style={{ width: '100%', minWidth: '600px', borderCollapse: 'collapse', border: '1px solid #ddd', tableLayout: 'fixed' }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                {table.inputColumns.map((column, index) => (
                  <th key={index} style={{ 
                    padding: '8px', 
                    border: '1px solid #ddd', 
                    textAlign: 'left',
                    width: `${80 / (table.inputColumns.length + 1)}%`
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <IonText>
                        <strong>{getColumnDisplayNameEnhanced(column)}</strong>
                      </IonText>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {onDeleteColumn && index === table.inputColumns.length - 1 && (
                          <IonButton
                            fill="clear"
                            size="small"
                            color="danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteColumn(index);
                            }}
                            style={{ minWidth: '24px', height: '24px' }}
                          >
                            <IonIcon icon={trash} />
                          </IonButton>
                        )}
                      </div>
                    </div>
                  </th>
                ))}
                <th style={{ 
                  padding: '8px', 
                  border: '1px solid #ddd', 
                  textAlign: 'left',
                  width: '40%'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <IonText>
                      <strong>Output</strong>
                    </IonText>
                    {onAddColumn && (
                      <IonButton
                        fill="clear"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddColumn();
                        }}
                      >
                        <IonIcon icon={add} />
                        {' add column'}
                      </IonButton>
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {paths.map((path, rowIndex) => (
                <tr key={rowIndex}>
                  {table.inputColumns.map((column, colIndex) => {
                    const mergeCount = getMergeCount(paths, rowIndex, colIndex);
                    
                    // Check if this is the first row in a merge group
                    let isFirstInMerge = true;
                    if (rowIndex > 0) {
                      const currentPath = paths[rowIndex];
                      const previousPath = paths[rowIndex - 1];
                      
                      // Check if all decisions up to and including the current column are identical
                      for (let j = 0; j <= colIndex; j++) {
                        if (JSON.stringify(currentPath.decisions[j]) !== JSON.stringify(previousPath.decisions[j])) {
                          isFirstInMerge = true;
                          break;
                        }
                      }
                      
                      // If all decisions are identical, this is not the first in merge
                      if (isFirstInMerge) {
                        let allIdentical = true;
                        for (let j = 0; j <= colIndex; j++) {
                          if (JSON.stringify(currentPath.decisions[j]) !== JSON.stringify(previousPath.decisions[j])) {
                            allIdentical = false;
                            break;
                          }
                        }
                        isFirstInMerge = !allIdentical;
                      }
                    }
                    
                    if (!isFirstInMerge) {
                      return null; // Skip rendering merged cells
                    }

                    return (
                      <td
                        key={colIndex}
                        rowSpan={mergeCount}
                        style={{ 
                          verticalAlign: 'top',
                          padding: '8px',
                          border: '1px solid #ddd',
                          backgroundColor: 'transparent',
                          cursor: isCellClickable(rowIndex, colIndex) ? 'pointer' : 'default'
                        }}
                        onClick={isCellClickable(rowIndex, colIndex) ? () => handleCellClick(rowIndex, colIndex) : undefined}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <IonText>
                            {(() => {
                              const decision = path.decisions[colIndex];
                              const column = table.inputColumns[colIndex];
                              const isDiscreteColumn = ['categorical', 'lightStatus', 'tags'].includes(column?.type);
                              
                              if (isDiscreteColumn && isDefaultDecision(decision)) {
                                const targetPath = paths[rowIndex];
                                const usedOptions = getUsedOptionsForColumnAtPath(table.decisionTree, targetPath, colIndex);
                                const remainingOption = getRemainingOption(column, usedOptions, tags || [], parameters || []);
                                
                                if (remainingOption) {
                                  return `other (${remainingOption})`;
                                }
                              }
                              
                              return customFormatDecision(decision, column);
                            })()}
                          </IonText>
                          {(() => {
                            const decision = path.decisions[colIndex];
                            const column = table.inputColumns[colIndex];
                            const isDiscreteColumn = ['categorical', 'lightStatus', 'tags'].includes(column?.type);
                            const isBooleanColumn = column?.type === 'tags';
                            
                            // For discrete columns with existing decisions, show delete button
                            if (isDiscreteColumn && !isDefaultDecision(decision)) {
                              return (
                                <IonButton
                                  fill="clear"
                                  size="small"
                                  color="danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (onDeleteBranch) {
                                      onDeleteBranch(rowIndex, colIndex);
                                    }
                                  }}
                                >
                                  <IonIcon icon={trash} />
                                </IonButton>
                              );
                            }
                            
                            // For boolean columns with no decision (not split yet), show add button that creates split directly
                            if (isBooleanColumn && !decision) {
                              return (
                                <IonButton
                                  fill="clear"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (onCreateSplit) {
                                      onCreateSplit(rowIndex, colIndex, 'true');
                                    }
                                  }}
                                >
                                  <IonIcon icon={add} />
                                  {' add split'}
                                </IonButton>
                              );
                            }
                            
                            // For boolean columns that have been split, never show add button (only delete buttons)
                            if (isBooleanColumn && decision) {
                              return null; // Boolean columns can only be split once (true/false)
                            }
                            
                            // Hide button if it's a non-boolean discrete column with one or no available options
                            if (isDiscreteColumn && !isBooleanColumn && isDefaultDecision(decision)) {
                              const targetPath = paths[rowIndex];
                              const usedOptions = getUsedOptionsForColumnAtPath(table.decisionTree, targetPath, colIndex);
                              const availableOptions = getAvailableOptions(column, usedOptions, tags || [], parameters || []);
                              
                              if (availableOptions.length <= 1) {
                                return null; // Hide the button when 1 or 0 options remain
                              }
                            }
                            
                            // For non-discrete columns or other discrete columns with available options, show add/edit button
                            return (
                              <IonButton
                                fill="clear"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCellClick(rowIndex, colIndex);
                                }}
                              >
                                <IonIcon icon={isDefaultDecision(decision) ? add : create} />
                                {isDefaultDecision(decision) ? ' add split' : ''}
                              </IonButton>
                            );
                          })()}
                        </div>
                      </td>
                    );
                  })}
                  
                  {/* Output Cell */}
                  <td
                    style={{
                      verticalAlign: 'top',
                      padding: '8px',
                      border: '1px solid #ddd',
                      backgroundColor: '#f9f9f9'
                    }}
                    onClick={() => onEditOutput && onEditOutput(rowIndex, path.output)}
                  >
                    {renderOutputCell(path.output, rowIndex)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>


        {/* Modal for creating new split conditions */}
        <IonModal isOpen={isCreateModalOpen} onDidDismiss={() => setIsCreateModalOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Create Split Condition</IonTitle>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {modalData && (() => {
              const column = modalData.column;
              const targetPath = paths[modalData.row];
              const usedOptions = getUsedOptionsForColumnAtPath(table.decisionTree, targetPath, modalData.col);
              const availableOptions = getAvailableOptions(column, usedOptions, tags || [], parameters || []);
              const isDiscreteColumn = ['categorical', 'lightStatus', 'tags'].includes(column.type);
              
              return (
                <div>
                  <IonItem>
                    <IonLabel position="stacked">
                      Column: {getColumnDisplayNameEnhanced(column)}
                    </IonLabel>
                  </IonItem>
                  
                  {isDiscreteColumn ? (
                    <IonItem>
                      <IonLabel position="stacked">
                        {column.type === 'categorical' || column.type === 'lightStatus' ? 'Category' : 'Value'}
                      </IonLabel>
                      <IonSelect
                        value={newSplitValue}
                        onIonChange={e => setNewSplitValue(e.detail.value)}
                        placeholder="Select option"
                      >
                        {availableOptions.map(option => (
                          <IonSelectOption key={option} value={option}>
                            {option}
                          </IonSelectOption>
                        ))}
                      </IonSelect>
                    </IonItem>
                  ) : (
                    <IonItem>
                      <IonLabel position="stacked">
                        {column.type === 'ratio' ? 'Maximum Percentage' : column.type === 'numerical' || column.type === 'derived' ? 'Maximum Value' : 'Value'}
                      </IonLabel>
                      <IonInput
                        value={newSplitValue}
                        onIonInput={e => setNewSplitValue(e.detail.value ?? '')}
                        placeholder={column.type === 'ratio' ? 'Enter percent (e.g., 25)' : column.type === 'numerical' || column.type === 'derived' ? 'Enter number' : 'Enter value'}
                      />
                    </IonItem>
                  )}

                  {isDiscreteColumn && availableOptions.length === 0 && (
                    <IonItem color="warning">
                      <IonLabel>
                        All available options for this column have already been used.
                      </IonLabel>
                    </IonItem>
                  )}

                  <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                    <IonButton
                      expand="block"
                      onClick={handleCreateSplit}
                      disabled={!newSplitValue.trim() || (isDiscreteColumn && availableOptions.length === 0)}
                    >
                      Create Split
                    </IonButton>
                    <IonButton
                      expand="block"
                      fill="outline"
                      onClick={() => setIsCreateModalOpen(false)}
                    >
                      Cancel
                    </IonButton>
                  </div>
                </div>
              );
            })()}
          </IonContent>
        </IonModal>

        {/* Modal for editing existing split conditions */}
        <IonModal isOpen={isEditModalOpen} onDidDismiss={() => setIsEditModalOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Edit Split Condition</IonTitle>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {modalData && (() => {
              const column = modalData.column;
              const targetPath = paths[modalData.row];
              const usedOptions = getUsedOptionsForColumnAtPath(table.decisionTree, targetPath, modalData.col);
              const availableOptions = getAvailableOptions(column, usedOptions, tags || [], parameters || []);
              const isDiscreteColumn = ['categorical', 'lightStatus', 'tags'].includes(column.type);
              
              return (
                <div>
                  <IonItem>
                    <IonLabel position="stacked">
                      Column: {getColumnDisplayNameEnhanced(column)}
                    </IonLabel>
                  </IonItem>
                  
                  {isDiscreteColumn ? (
                    <IonItem>
                      <IonLabel position="stacked">
                        {column.type === 'categorical' || column.type === 'lightStatus' ? 'Category' : 'Value'}
                      </IonLabel>
                      <IonSelect
                        value={newSplitValue}
                        onIonChange={e => setNewSplitValue(e.detail.value)}
                        placeholder="Select option"
                      >
                        {availableOptions.map(option => (
                          <IonSelectOption key={option} value={option}>
                            {option}
                          </IonSelectOption>
                        ))}
                      </IonSelect>
                    </IonItem>
                  ) : (
                    <IonItem>
                      <IonLabel position="stacked">
                        {column.type === 'ratio' ? 'Maximum Percentage' : column.type === 'numerical' || column.type === 'derived' ? 'Maximum Value' : 'Value'}
                      </IonLabel>
                      <IonInput
                        value={newSplitValue}
                        onIonInput={e => setNewSplitValue(e.detail.value ?? '')}
                        placeholder={column.type === 'ratio' ? 'Enter percent (e.g., 25)' : column.type === 'numerical' || column.type === 'derived' ? 'Enter number' : 'Enter value'}
                      />
                    </IonItem>
                  )}

                  {isDiscreteColumn && availableOptions.length === 0 && (
                    <IonItem color="warning">
                      <IonLabel>
                        All available options for this column have already been used.
                      </IonLabel>
                    </IonItem>
                  )}

                  <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                    <IonButton
                      expand="block"
                      onClick={handleEditSplit}
                      disabled={!newSplitValue.trim() || (isDiscreteColumn && availableOptions.length === 0)}
                    >
                      Update Split
                    </IonButton>
                    <IonButton
                      expand="block"
                      fill="outline"
                      color="danger"
                      onClick={handleDeleteBranch}
                    >
                      <IonIcon slot="start" icon={trash} />
                      Delete Branch
                    </IonButton>
                    <IonButton
                      expand="block"
                      fill="outline"
                      onClick={() => setIsEditModalOpen(false)}
                    >
                      Cancel
                    </IonButton>
                  </div>
                </div>
              );
            })()}
          </IonContent>
        </IonModal>
    </div>
  );
};

export default DecisionTreeTable;
